import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createPlacementsClient } from './api';
import {
  placementStatusCountsFilter,
  type PlacementLookupFilter,
  type PlacementsFilter,
} from './model';

export const placementsQueryKey = ['supply', 'placements'] as const;
export const placementsListsQueryKey = [...placementsQueryKey, 'list'] as const;
export const placementStatusCountsQueryKey = [...placementsQueryKey, 'status-counts'] as const;
export const placementAdUnitConfigQueryKey = [...placementsQueryKey, 'ad-unit-config'] as const;

export function usePlacementsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createPlacementsClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function usePlacementsQuery(filter: PlacementsFilter) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementsListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function usePlacementStatusCountsQuery(filter: PlacementsFilter) {
  const client = usePlacementsClient();
  const countFilter = placementStatusCountsFilter(filter);
  return useQuery({
    queryKey: [...placementStatusCountsQueryKey, countFilter],
    queryFn: () => client.getStatusCounts(countFilter),
  });
}

export function usePlacementQuery(id: string | undefined) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementsQueryKey, 'detail', id],
    queryFn: () => client.get(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function usePlacementPublishersQuery(filter: PlacementLookupFilter, enabled = true) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementsQueryKey, 'lookup', 'publishers', filter],
    queryFn: () => client.searchPublishers(filter),
    enabled,
  });
}

export function usePlacementIntegrationsQuery(filter: PlacementLookupFilter, enabled = true) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementsQueryKey, 'lookup', 'integrations', filter],
    queryFn: () => client.searchDemandIntegrations(filter),
    enabled,
  });
}

export function usePlacementTargetingsQuery(filter: PlacementLookupFilter, enabled = true) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementsQueryKey, 'lookup', 'targetings', filter],
    queryFn: () => client.searchTargetings(filter),
    enabled,
  });
}

export function usePlacementIabCategoriesQuery(enabled = true) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementsQueryKey, 'lookup', 'iab-categories'],
    queryFn: () => client.listIabCategories(),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function usePlacementAssignedTargetingsQuery(
  placementId: string,
  filter: PlacementLookupFilter,
  enabled = true,
) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementsQueryKey, 'detail', placementId, 'targetings', filter],
    queryFn: () => client.listPlacementTargetings(placementId, filter),
    enabled: enabled && Boolean(placementId),
  });
}

export function usePlacementEditorLookups(publisherId: string, adUnitEnabled: boolean) {
  const client = usePlacementsClient();
  return {
    widgets: useQuery({
      queryKey: [...placementsQueryKey, 'lookup', 'widgets'],
      queryFn: () => client.listWidgetTypes(),
      enabled: adUnitEnabled,
    }),
    domains: useQuery({
      queryKey: [...placementsQueryKey, 'lookup', 'publisher-domains', publisherId],
      queryFn: () => client.listPublisherDomains(publisherId),
      enabled: adUnitEnabled && Boolean(publisherId),
    }),
  };
}

export function usePlacementAdUnitConfigQuery(id: string | undefined, enabled: boolean) {
  const client = usePlacementsClient();
  return useQuery({
    queryKey: [...placementAdUnitConfigQueryKey, id],
    queryFn: () => client.getAdUnitConfig(id ?? ''),
    enabled: enabled && Boolean(id),
  });
}

export function usePlacementMutations() {
  const client = usePlacementsClient();
  const queryClient = useQueryClient();
  const invalidateListsAndCounts = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: placementsListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: placementStatusCountsQueryKey }),
    ]);
  return {
    create: useMutation({ mutationFn: client.create, onSuccess: invalidateListsAndCounts }),
    update: useMutation({ mutationFn: client.update, onSuccess: invalidateListsAndCounts }),
    setArchived: useMutation({
      mutationFn: client.setArchived,
      onSuccess: invalidateListsAndCounts,
    }),
    buildAdUnit: useMutation({
      mutationFn: client.buildAdUnit,
      onSuccess: (_data, id) =>
        queryClient.invalidateQueries({
          queryKey: [...placementAdUnitConfigQueryKey, id],
        }),
    }),
  };
}
